import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const validation = useMemo(() => {
    if (!newPassword) return { ok: false, msg: '' };
    if (newPassword.length < 8) return { ok: false, msg: 'Use at least 8 characters.' };
    if (confirmPassword && confirmPassword !== newPassword) return { ok: false, msg: "Passwords don't match." };
    return { ok: true, msg: confirmPassword ? 'Looks good.' : 'Confirm password to continue.' };
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Reset link is missing the token. Please request a new email.');
      return;
    }
    if (!validation.ok || !confirmPassword) {
      setError(validation.msg || 'Please complete both password fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not reset password. Please request a new link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-160px)] flex items-center justify-center px-4 py-16"
      style={{ backgroundColor: '#f7f9f3' }}
      data-testid="admin-reset-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-emerald-100"
      >
        <div
          className="px-8 py-6 text-white"
          style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm uppercase tracking-wider opacity-80">IIY Admin</div>
              <div className="text-xl font-semibold">Choose a new password</div>
            </div>
          </div>
        </div>

        {success ? (
          <div className="px-8 py-10 text-center" data-testid="admin-reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Password updated</h3>
            <p className="text-sm text-slate-600 mb-4">You can now sign in with your new password. Redirecting…</p>
            <Link to="/admin/login" className="text-emerald-700 font-medium hover:underline">Go to sign-in</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5" data-testid="admin-reset-form">
            <p className="text-sm text-slate-600">Enter a new password (min 8 characters). The reset link expires in 1 hour and can be used only once.</p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={submitting}
                  data-testid="admin-reset-new-password"
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-2.5 p-1 text-slate-500 hover:text-slate-700"
                  aria-label={showNew ? 'Hide password' : 'Show password'}
                  data-testid="admin-reset-toggle-new"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={submitting}
                  data-testid="admin-reset-confirm-password"
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-600 transition"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-2.5 p-1 text-slate-500 hover:text-slate-700"
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  data-testid="admin-reset-toggle-confirm"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && (
                <p className={`text-xs mt-1.5 ${validation.ok ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="admin-reset-validation">
                  {validation.msg}
                </p>
              )}
            </div>

            {error && (
              <div className="text-sm rounded-lg px-3 py-2.5 bg-red-50 border border-red-200 text-red-700 flex items-start gap-2" data-testid="admin-reset-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !validation.ok || !confirmPassword}
              data-testid="admin-reset-submit"
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-white font-medium transition disabled:opacity-60"
              style={{
                background: submitting ? '#7f9b66' : 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)',
                cursor: submitting || !validation.ok ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {submitting ? 'Updating…' : 'Update password'}
            </button>

            <p className="text-xs text-slate-500 text-center pt-2">
              <Link to="/admin/login" className="text-emerald-700 hover:underline">Back to sign-in</Link>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
